import { isDefined } from '@stillmandigital/common';
import { buildMenuItems, MenuItem, SidebarPlatform } from './sidebar.constants';

export function getMenuItems(platforms: SidebarPlatform[] = []): MenuItem[] {
  const root = buildMenuItems(platforms);
  return root.map(item => patchMenuItem(item, root));
}

export function getLeafMenuItems(platforms: SidebarPlatform[] = []): MenuItem[] {
  return flattenNodes(getMenuItems(platforms)).filter(item => !item.children?.length);
}

export function getAllMenuItems(platforms: SidebarPlatform[] = []): MenuItem[] {
  return flattenNodes(getMenuItems(platforms));
}

const DEFAULT_PINNED_TOP_LEVEL_GROUPS = ['TRADING', 'ACCOUNTS', 'TRANSFERS'];

export function getDefaultPinnedMenuItemIds(): string[] {
  return getLeafMenuItems()
    .filter(item => !!item.groupPath?.length && DEFAULT_PINNED_TOP_LEVEL_GROUPS.includes(item.groupPath[0]))
    .map(item => item.fullPath!);
}

export interface PinnedGroup {
  label: string;
  items: MenuItem[];
  children: PinnedGroup[];
}

export interface PinnedGroups {
  ungrouped: MenuItem[];
  groups: PinnedGroup[];
}

export function groupPinnedItems(items: MenuItem[]): PinnedGroups {
  const ungrouped: MenuItem[] = [];
  const groups: PinnedGroup[] = [];

  for (const item of items) {
    const path = item.groupPath ?? [];

    if (!path.length) {
      ungrouped.push(item);
      continue;
    }

    let siblings = groups;
    let group: PinnedGroup | undefined;

    for (const segment of path) {
      group = siblings.find(existing => existing.label === segment);
      if (!group) {
        group = { label: segment, items: [], children: [] };
        siblings.push(group);
      }
      siblings = group.children;
    }

    group!.items.push(item);
  }

  return { ungrouped, groups };
}

function patchMenuItem(item: MenuItem, root: MenuItem[]) {
  const patched = {
    ...item,
    fullPath: getFullPath(item, root),
    groupPath: getGroupPath(item, root),
  };

  if (item.children?.length) {
    patched.children = item.children.map(child => patchMenuItem(child, root));
  }

  return patched;
}

function getGroupPath(menuItem: MenuItem, root: MenuItem[]): string[] {
  return getAllParents(menuItem, root)
    .reverse()
    .map(parent => parent.name);
}

function getFullPath(menuItem: MenuItem, root: MenuItem[]): string {
  const parents = getAllParents(menuItem, root)
    .reverse()
    .map(menuItem => menuItem.link);

  return [...parents, menuItem.link].filter(isDefined).join('/');
}

function getAllParents(menuItem: MenuItem, root: MenuItem[], parents: MenuItem[] = []): MenuItem[] {
  const parent = getParentNode(menuItem, root);
  if (parent) {
    parents.push(parent);
    getAllParents(parent, root, parents);
  }

  return parents;
}

function getParentNode(node: MenuItem | undefined, root: MenuItem[]) {
  if (isDefined(node)) {
    for (const parent of flattenNodes(root)) {
      if (parent.children?.includes(node)) {
        return parent;
      }
    }
  }

  return undefined;
}

function flattenNodes(nodes: MenuItem[]): MenuItem[] {
  const flattenedNodes = [];
  for (const node of nodes) {
    flattenedNodes.push(node);
    if (node.children) {
      flattenedNodes.push(...flattenNodes(node.children));
    }
  }
  return flattenedNodes;
}
